/**
 * O mínimo que o helper lê de um `CdkDragDrop`. **Estrutural de propósito:** o tipo do CDK é
 * genérico no dado da lista (`CdkDragDrop<Order[]>`, `CdkDragDrop<Square>`) e genérico em posição de
 * parâmetro não aceita subtipo — tipar por `CdkDragDrop<unknown>` reprovaria todo chamador real no
 * `typecheck`, que foi exatamente o que aconteceu.
 */
export interface CardDropEvent {
  previousContainer: unknown;
  container: unknown;
  item: { data?: unknown };
}

/**
 * 🔴 Atraso para o arrasto começar **no toque**, zero no mouse — o mesmo da reordenação em tabela
 * (F15, `table-tree.component.html`).
 *
 * Sem ele, rolar a tela no tablet vira arrasto acidental: o dedo encosta no cartão para empurrar a
 * lista e o CDK entende que o item está sendo movido. No mouse o atraso é zero porque lá o gesto de
 * rolar é a roda, não o clique.
 */
export const DRAG_START_DELAY = { touch: 200, mouse: 0 };

/**
 * Classe que quem arrasta põe no `<body>` enquanto o item está **sobre um alvo que aceita**.
 *
 * Vai no `<body>` porque o preview é anexado lá pelo CDK: nenhum seletor a partir do componente o
 * alcança, e o CDK não reavalia `cdkDragPreviewClass` depois de criar o preview.
 */
export const DRAG_OVER_TARGET_CLASS = 'hmax-drag-over-target';

/**
 * Estilos do cartão arrastável, compartilhados pelo kanban de pedidos (M2-F1) e pelo mapa de contas
 * (M2-F2). Quem usa declara `encapsulation: ViewEncapsulation.None` (o preview é anexado ao `<body>` e,
 * com encapsulamento, o atributo do componente não chega nele) e aplica no template as **três** classes
 * que este bloco estiliza: `hmax-draggable` no cartão, `hmax-drag-preview` em `cdkDragPreviewClass` e
 * `hmax-drag-over-target` no `<body>` enquanto o item paira sobre um alvo válido.
 *
 * Os nomes ficam literais no template de propósito: constante exportada para colar numa `class=` seria
 * encanamento que ninguém consome, e renomeá-la deixaria o build verde com o cursor e o preview
 * quebrados nas duas telas.
 *
 * ⚠️ **Sem crase nos comentários deste bloco:** o CSS inline vira módulo virtual no JIT do Vitest, e
 * uma crase aqui derruba a SUÍTE INTEIRA com erro de parse — que o `nx build` não pega, porque no AOT
 * o estilo não passa por esse caminho.
 */
export const DRAG_CARD_STYLES = `
  .hmax-drag-preview {
    border-radius: 1rem;
    box-shadow: 0 8px 24px rgb(0 0 0 / 0.18);
    opacity: 0.92;
    transition: opacity 150ms ease;
    /* O cursor fica sobre o preview enquanto arrasta, entao e nele que a mao fechada mora. */
    cursor: grabbing;
  }

  /*
    Sobre um alvo que aceita, o item no ar apaga: quem manda na leitura passa a ser o alvo, que esta
    mostrando o que vai acontecer. Sair do alvo devolve a opacidade, e e essa ida e volta que diz ao
    operador que ele esta em cima de algo valido.
  */
  .hmax-drag-over-target .hmax-drag-preview {
    opacity: 0.45;
  }

  /*
    A mao aberta so em cartao que REALMENTE arrasta.

    A condicao e a classe que o proprio CDK gerencia a partir do cdkDragDisabled, e nao uma copia da
    regra no template: com duas fontes, um cartao que nao tem para onde ir convidaria ao arrasto com
    o cursor e nao sairia do lugar — a pior combinacao possivel, porque o operador conclui que a tela
    travou.
  */
  .hmax-draggable.cdk-drag:not(.cdk-drag-disabled) {
    cursor: grab;
  }

  .hmax-draggable.cdk-drag:not(.cdk-drag-disabled):active {
    cursor: grabbing;
  }

  /* Os itens que ficam abrem espaco com animacao em vez de saltar. */
  .cdk-drop-list-dragging .hmax-draggable:not(.cdk-drag-placeholder) {
    transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
  }
`;

/**
 * O que foi solto, **quando a soltura significa alguma coisa**.
 *
 * Devolve `null` quando o item voltou para a própria lista — soltar onde já estava é no-op nas duas
 * telas que usam isto: no kanban a ordem é por antiguidade, e no mapa transferir uma conta para a
 * mesa em que ela já está não é uma operação.
 *
 * ⚠️ **Nada de `transferArrayItem` aqui, e nem em quem chama.** As listas das duas telas vêm de
 * `apiResource` e são dado de servidor: mover item entre os arrays criaria uma segunda fonte de
 * verdade, que o próximo poll desfaz. Quem move o cartão é a resposta do servidor ao evento que este
 * helper devolve.
 */
export function resolveCardDrop<T>(event: CardDropEvent): T | null {
  if (event.previousContainer === event.container) {
    return null;
  }

  return (event.item.data as T | undefined) ?? null;
}
